/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { push } from '@lagunovsky/redux-react-router';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import CursorLeft from '../../../icons/CursorLeft';
import CursorRight from '../../../icons/CursorRight';
import { TournamentRoundType } from '../../../reactutils/withTournamentRoundSelection';
import { TournamentRound } from '../../../services/betting';
import { getTournamentApi2 } from '../../System/systemConfig';
import {
    tournamentSelectionNextTournamentRoundId,
    tournamentSelectionPrevTournamentRoundId,
    tournamentSelectionVerifyAndAdjustSelectedTournamentByTrId,
} from '../actions';
import {
    tournamentSelectionAvailableTournamentRoundSelector,
    tournamentSelectionNextTournamentRoundIdSelector,
    tournamentSelectionPrevTournamentRoundIdSelector,
    tournamentSelectionSelectedTournamentRoundIdSelector,
} from '../selectors';

interface Props2 {
    type: TournamentRoundType;
    trId: string;
    pathByTournamentRound: (trId: string) => string;
}

export const TournamentRoundSelectionComponent2: React.FC<Props2> = (props) => {
    const dispatch = useDispatch();

    const availableTournamentRounds = useSelector(tournamentSelectionAvailableTournamentRoundSelector(props.type));

    const api = getTournamentApi2();

    const [nextRound, setNextRound] = React.useState<TournamentRound>();
    const [priorRound, setPriorRound] = React.useState<TournamentRound>();

    const nextTournamentRoundQuery = useQuery({
        queryKey: ['nextTournamentRound', props.type, props.trId],
        queryFn: () => api.getNextTournamentRoundByType({trId: Number(props.trId), type: props.type}),
        refetchOnWindowFocus: false,
    });

    React.useEffect(() => {
        setNextRound(nextTournamentRoundQuery.data);
    }, [nextTournamentRoundQuery.data]);

    const priorTournamentRoundQuery = useQuery({
        queryKey: ['priorTournamentRound', props.type, props.trId],
        queryFn: () => api.getPreviousTournamentRoundByType({trId: Number(props.trId), type: props.type}),
        refetchOnWindowFocus: false,
    });

    React.useEffect(() => {
        setPriorRound(priorTournamentRoundQuery.data);
    }, [priorTournamentRoundQuery.data]);

    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    React.useEffect(() => {
        dispatch(tournamentSelectionVerifyAndAdjustSelectedTournamentByTrId(props.trId));
    }, [props, dispatch]);

    const isNextEnabled = (): boolean => {
        return nextRound !== undefined && nextRound.id !== undefined;
    };

    const isPriorEnabled = (): boolean => {
        return priorRound !== undefined && priorRound.id !== undefined;
    };

    const onNextRound = () => {
        if (isNextEnabled()) {
            dispatch(push(props.pathByTournamentRound(String(nextRound?.id))));
        }
    };
    const onPrevRound = () => {
        if (isPriorEnabled()) {
            dispatch(push(props.pathByTournamentRound(String(priorRound?.id))));
        }
    };
    const onTournamentRoundIdChange = (trId: string) => {
        dispatch(push(props.pathByTournamentRound(trId)));
    };

    const getSelectedTournamentRoundName = () => {
        const tr = availableTournamentRounds && availableTournamentRounds.find((tr) => String(tr.id) === props.trId);
        return (tr && tr.longName) || '';
    };

    return (
        <div className="d-flex flex-nowrap align-items-center justify-content-end tournamentRoundSelectionComponent pb-2">
            <Button className="btn-r1gray" size="sm" outline={true} onClick={onPrevRound} disabled={!isPriorEnabled()}>
                <CursorLeft />
            </Button>

            <Dropdown
                className="ps-2 pe-2"
                isOpen={dropdownOpen}
                toggle={() => setDropdownOpen(!dropdownOpen)}
                size="sm"
                direction={'down'}
            >
                <DropdownToggle className="btn-r1gray" caret={true}>
                    {getSelectedTournamentRoundName()}
                </DropdownToggle>
                {availableTournamentRounds && availableTournamentRounds.length > 0 && (
                    <DropdownMenu style={{ zIndex: 4000 }}>
                        {availableTournamentRounds &&
                            availableTournamentRounds.map((tr) => (
                                <DropdownItem key={tr.id} onClick={() => onTournamentRoundIdChange(String(tr.id))}>
                                    {tr.longName}
                                </DropdownItem>
                            ))}
                    </DropdownMenu>
                )}
            </Dropdown>
            <Button className="btn-r1gray" size="sm" outline={true} onClick={onNextRound} disabled={!isNextEnabled()}>
                <CursorRight />
            </Button>
        </div>
    );
};

interface Props {
    type: TournamentRoundType;
    pathByTournamentRound: (props: any) => (trId: string) => string;
    onTournamentRoundIdSelected: (trId: string) => void;
}

export const TournamentRoundSelectionComponent: React.FC<Props> = (props) => {
    const availableTournamentRounds = useSelector(tournamentSelectionAvailableTournamentRoundSelector(props.type));
    const selectedTournamentRoundId = useSelector(tournamentSelectionSelectedTournamentRoundIdSelector(props.type));

    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    const previousRoundAvailable =
        useSelector(tournamentSelectionPrevTournamentRoundIdSelector(props.type)) !== undefined;
    const nextRoundAvailable = useSelector(tournamentSelectionNextTournamentRoundIdSelector(props.type)) !== undefined;

    const dispatch = useDispatch();

    const onNextRound = () => {
        dispatch(
            tournamentSelectionNextTournamentRoundId({
                type: props.type,
                nextPath: props.pathByTournamentRound(props),
            }),
        );
    };
    const onPrevRound = () => {
        dispatch(
            tournamentSelectionPrevTournamentRoundId({
                type: props.type,
                nextPath: props.pathByTournamentRound(props),
            }),
        );
    };
    const onTournamentRoundIdChange = (trId: string) => {
        if (props.onTournamentRoundIdSelected) {
            props.onTournamentRoundIdSelected(trId);
        }
    };

    const getSelectedTournamentRoundName = () => {
        const tr =
            availableTournamentRounds &&
            availableTournamentRounds.find((tr) => String(tr.id) === selectedTournamentRoundId);
        return (tr && tr.longName) || '';
    };

    return (
        <>
            {' '}
            <div className="d-flex flex-nowrap align-items-center justify-content-end tournamentRoundSelectionComponent pb-2">
                <Button
                    className="btn-r1gray"
                    size="sm"
                    outline={true}
                    onClick={onPrevRound}
                    disabled={!previousRoundAvailable}
                >
                    <CursorLeft />
                </Button>

                <Dropdown
                    className="ps-2 pe-2"
                    isOpen={dropdownOpen}
                    toggle={() => setDropdownOpen(!dropdownOpen)}
                    size="sm"
                    direction={'down'}
                >
                    <DropdownToggle className="btn-r1gray" caret={true}>
                        {getSelectedTournamentRoundName()}
                    </DropdownToggle>
                    {availableTournamentRounds && availableTournamentRounds.length > 0 && (
                        <DropdownMenu style={{ zIndex: 4000 }}>
                            {availableTournamentRounds &&
                                availableTournamentRounds.map((tr) => (
                                    <DropdownItem key={tr.id} onClick={() => onTournamentRoundIdChange(String(tr.id))}>
                                        {tr.longName}
                                    </DropdownItem>
                                ))}
                        </DropdownMenu>
                    )}
                </Dropdown>
                <Button
                    className="btn-r1gray"
                    size="sm"
                    outline={true}
                    onClick={onNextRound}
                    disabled={!nextRoundAvailable}
                >
                    <CursorRight />
                </Button>
            </div>
        </>
    );
};
